import React, { useState } from 'react';

import { getApi } from '../../../api';
import { useAuth } from '../../../context/AuthContext';
import { Fetch } from '../../../Fetch';
import type { AuthLoginData, LoginProps } from '../../../types';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { Logotype } from '../Logotype';

/**
 * Login Component - User authentication form
 *
 * Provides a login form for users to authenticate with username and password.
 * Remembers the last username used and validates form inputs before submission.
 * On successful login, updates the authentication context and navigates to map.
 */
export const Login: React.FC<LoginProps> = ({ socket, setView, setError }) => {
  const { login } = useAuth();

  // Initialize username from localStorage (remember last user)
  const initialUsername = window.localStorage.getItem('username') || '';
  const [submit, setSubmit] = useState(false);
  const [username, setUsernameLocal] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Handle login error - reset form and show error message
   */
  const handleError = (error: Error | { error: string } | string): void => {
    setSubmit(false);
    // Convert error to the format expected by setError
    if (error instanceof Error) {
      setError(error);
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      setError(error.error);
    }
  };

  /**
   * Handle successful login - update auth context and navigate to map
   */
  const handleResult = (data: unknown): void => {
    const result = data as AuthLoginData;
    // Emit token to socket for real-time updates
    if (socket) {
      socket.emit('token', { token: result.token });
    }

    // Update authentication state and navigate to map
    login(result);
    setSubmit(false);
    setView('map');
  };

  // Form validation - username must be at least 2 chars, password at least 4 chars
  const isFormValid = username.length >= 2 && password.length >= 4;

  /**
   * Handle form submission (both button click and Enter key)
   */
  const handleSubmit = (): void => {
    if (isFormValid && !submit) {
      setSubmit(true);
    }
  };

  /**
   * Handle Enter key press in form fields
   */
  const handleKeyPress = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div style={{ minWidth: '50vmin', width: '50vmin' }}>
      <Logotype />
      <hr />
      <div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            key="username"
            hint="Username"
            value={username}
            type="text"
            onChange={setUsernameLocal}
            disabled={submit}
            onKeyPress={handleKeyPress}
          />
          <div style={{ position: 'relative' }}>
            <Input
              key="password"
              hint="Password"
              value={password}
              type={showPassword ? 'text' : 'password'}
              onChange={setPassword}
              disabled={submit}
              onKeyPress={handleKeyPress}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '2vmin',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '2vmin',
                color: '#666',
                padding: '0.5vmin',
                marginTop: '1vmin',
              }}
              disabled={submit}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
        </form>
      </div>
      <hr />
      <Button text="Login" onClick={handleSubmit} disabled={submit || !isFormValid} />
      <Fetch
        submit={submit}
        method="POST"
        body={{ username, password }}
        onError={handleError}
        onResult={handleResult}
        api={`${getApi()}/login`}
      />
    </div>
  );
};

export default Login;
